import * as React from 'react';
import type { FormattedMessage } from 'react-intl';

import type { completeSignup } from 'state/actions/complete-signup';
import { ScreenName } from '@atlassian/help-center-common-component/constants';
import type { InjectedProps as BrowserMetricsScreenTransitionStatusProps } from '@atlassian/help-center-common-util/analytics/browsermetrics-screen-transition-status-context';
import type { DefaultError } from '@atlassian/help-center-common-util/errors';
import {
    validateAll,
    validateFullName,
    validatePassword,
} from '@atlassian/help-center-common-util/sign-up-form-validator';
import { CompleteSignupFormStateless } from '../complete-signup-form-stateless';

interface CompleteSignupFormProps {
    signupToken: string;
    portalId?: number;
    email: string;
    isSubmitting: boolean;
    submitError?: DefaultError;
    contactLink?: string;
    completeSignupAction: typeof completeSignup;
}

interface State {
    fullName: string;
    fullNameError?: FormattedMessage.MessageDescriptor;
    password: string;
    passwordError?: FormattedMessage.MessageDescriptor;
}

export default class CompleteSignupForm extends React.Component<
    CompleteSignupFormProps & BrowserMetricsScreenTransitionStatusProps,
    State
> {
    state = {
        fullName: '',
        fullNameError: undefined,
        password: '',
        passwordError: undefined,
    };

    onFullNameFieldChange = (updatedFullName: string) => {
        this.setState({
            fullName: updatedFullName,
        });

        if (this.state.fullNameError) {
            const updatedFullNameError = validateFullName(updatedFullName);
            this.setState({
                fullNameError: updatedFullNameError,
            });
        }
    };

    onPasswordFieldChange = (updatedPassword: string) => {
        this.setState({
            password: updatedPassword,
        });

        if (this.state.passwordError) {
            const updatedPasswordError = validatePassword(updatedPassword);
            this.setState({
                passwordError: updatedPasswordError,
            });
        }
    };

    validateFields = () => {
        const { fullName, password } = this.state;
        const validationErrors = validateAll(fullName, password);
        if (validationErrors) {
            this.setState({
                ...validationErrors,
            });
        } else {
            this.setState({
                fullNameError: undefined,
                passwordError: undefined,
            });
        }

        return validationErrors !== undefined;
    };

    onSubmit = () => {
        const validationErrorsPresent = this.validateFields();
        if (validationErrorsPresent) {
            return;
        }

        this.props.startBrowserMetricsTransitionToNewScreen(
            this.props.portalId ? ScreenName.PORTAL_HOME : ScreenName.HELP_CENTER
        );

        const { signupToken, contactLink, completeSignupAction } = this.props;
        const contactAdminsFormEnabled = contactLink !== undefined;

        const { fullName, password } = this.state;

        completeSignupAction(signupToken, fullName, password, this.props.email, contactAdminsFormEnabled);
    };

    render() {
        const { email, isSubmitting, submitError, contactLink } = this.props;
        const { fullName, fullNameError, password, passwordError } = this.state;

        return (
            <CompleteSignupFormStateless
                email={email}
                fullName={fullName}
                fullNameError={fullNameError}
                onFullNameChange={this.onFullNameFieldChange}
                password={password}
                passwordError={passwordError}
                onPasswordChange={this.onPasswordFieldChange}
                onSubmit={this.onSubmit}
                isSubmitting={isSubmitting}
                submitError={submitError}
                contactLink={contactLink}
            />
        );
    }
}
